import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useMediaQuery } from '@mui/material';
import ErrorHandler from '@Root/ErrorHandler';
import NameCardForm from '@RegistriesForms/NameCardForm'
import { BsPlusLg as Add } from 'react-icons/bs';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Pagination from '@mui/material/Pagination';
import { PiIdentificationCardLight as NameCardIcon } from "react-icons/pi";
import Search from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { IoClose as Close } from "react-icons/io5";
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import NameCardRow from '@RegistriesRepresentationalDisplay/NameCardRow';
import NameCardDisplay from '@RegistriesFunctionalDisplay/NameCardDisplay';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './NameCardPage.scss';

export default function NameCardPage(props) {

    const [showLoader, setShowLoader] = useState(false);
    const [showNameCardForm, setShowNameCardForm] = useState(false);
    const [showUserCard, setShowUserCard] = useState(true);
    const [nameCardList, setNameCardList] = useState([]);
    const [selectedNameCard, setSelectedNameCard] = useState();
    const { currentProjectId } = useReduxProject();
    const [searchQuery, setSearchQuery] = useState('');
    const defaultFilters = {
        'nameCardFilter': {
            'projectIdList': [currentProjectId],
            'sortBy': 'createTimeStamp',
            'sortAsc': false
        },
        'page': 0,
        'size': 10
    }
    const [page, setPage] = useState(0);
    const [size] = useState(10);
    const [filters, setFilters] = useState(defaultFilters);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResult, setTotalResult] = useState(1);
    const errorHandler = new ErrorHandler();
    const nameCardService = new NameCardService();
    const is991px = useMediaQuery('(max-width: 991px)');
    const is0To800 = useMediaQuery('(max-width: 800px)');
    const className = 'registries__nameCardPage';

    let interval;

    useEffect(() => {
        setShowLoader(true);
        renderData();
        interval = setInterval(() => {
            renderData();
        }, 20000);
        return () => {
            clearInterval(interval);
        }
    }, [])

    const renderData = async() => {
        try {
            let nameCardListObj = await nameCardService.getListFiltered(filters);
            nameCardListObj = JSON.parse(nameCardListObj.body);
            setNameCardList(nameCardListObj?.result?.nameCardList);
            setTotalPages(nameCardListObj?.result?.totalPages);
            setTotalResult(nameCardListObj?.result?.totalItems);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setShowLoader(false)
    }

    const reRenderData = async(newPage, newSize) => {
        try {
            setShowLoader(true);
            let filterObj = filters;
            filterObj.page = newPage - 1;
            filterObj.size = newSize;
            let nameCardListObj = await nameCardService.getListFiltered(filterObj);
            nameCardListObj = JSON.parse(nameCardListObj.body);
            setNameCardList(nameCardListObj?.result?.nameCardList);
            setTotalPages(nameCardListObj?.result?.totalPages);
            setTotalResult(nameCardListObj?.result?.totalItems);
        } catch(err) {
            errorHandler.handleError(err);
        }
        setShowLoader(false);
    }

    const toggleNameCardForm = () => {
        setShowNameCardForm(!showNameCardForm);
    }

    const handleSelectedNameCardChange = (nameCardObj) => {
        setSelectedNameCard(nameCardObj);
        props?.handleLabelData(nameCardObj);
    }

    const handleSearchQueryChange = (e) => {
        let sanitizedQuery = DOMPurify.sanitize(e.target.value);
        setSearchQuery(sanitizedQuery);
        let filtersObj = filters;
        filtersObj.nameCardFilter.searchQuery = sanitizedQuery;
        setFilters(filtersObj);
    }

    const handleSearch = () => {
        reRenderData(page + 1, size);
    }

    const handleSearchOnKeyPress = (e) => {
        if(e.keyCode === 13) {
            handleSearch();
        }
    }

    const handlePaginationClick = (e, v) => {
        setPage(v);
        reRenderData(v, size);
    }

    const clearSearch = () => {
        setSearchQuery('');
        setFilters(defaultFilters);
        renderData();
    }

    const handleShowUserCard = () => {
        setShowUserCard(prev => !prev);
    }

    return (
        <div className={className + '  height-wfa'}>
            <div className={`${className}__container`}>
                {/* Left Panel - List */}
                <div className={`${className}__listPanel`}>
                    {/* Header */}
                    <div className={`${className}__listPanel__header`}>
                        <div className={`${className}__listPanel__header__title`}>
                            <NameCardIcon className={`${className}__listPanel__header__icon`}/>
                            <div>
                                <Typography variant="h6" className={`${className}__listPanel__header__text`}>
                                    Name Cards
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'var(--color-primaryGrey)' }}>
                                    {totalResult} total items
                                </Typography>
                            </div>
                        </div>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={toggleNameCardForm}
                            className={`${className}__listPanel__header__createBtn`}
                        >
                            Create
                        </Button>
                    </div>

                    <Divider />

                    {/* Search */}
                    <div className={`${className}__listPanel__search`}>
                        <TextField
                            placeholder='Search name cards...'
                            value={searchQuery}
                            onChange={handleSearchQueryChange}
                            onKeyDown={handleSearchOnKeyPress}
                            size='small'
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search className={`${className}__listPanel__search__icon`} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position='end'>
                                        <Tooltip title="Clear search">
                                            <IconButton size='small' onClick={clearSearch}>
                                                <FilterAltOffOutlinedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                            className={`${className}__listPanel__search__input`}
                        />
                    </div>

                    {/* List */}
                    <div className={`${className}__listPanel__content`}>
                        {showLoader ? (
                            <div className={`${className}__listPanel__loader`}>
                                <LinearProgress />
                            </div>
                        ) : nameCardList?.length === 0 ? (
                            <div className={`${className}__listPanel__empty`}>
                                <NameCardIcon className={`${className}__listPanel__empty__icon`} />
                                <Typography variant="body1" sx={{ color: 'var(--color-primaryGrey)' }}>
                                    No name cards found
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={toggleNameCardForm}
                                    size="small"
                                >
                                    Create your first card
                                </Button>
                            </div>
                        ) : (
                            <div className={`${className}__listPanel__items`}>
                                {nameCardList?.map((nameCard, index) => (
                                    <NameCardRow
                                        key={index}
                                        nameCard={nameCard}
                                        selectedNameCard={selectedNameCard}
                                        setSelectedNameCard={handleSelectedNameCardChange}
                                    />
                                ))}
                            </div>
                        )}

                        <div className={`${className}__listPanel__addNew`} onClick={toggleNameCardForm}>
                            <Add className={`${className}__listPanel__addNew__icon`}/>
                            <span>Create New Name Card</span>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className={`${className}__listPanel__footer`}>
                            <Divider />
                            <div className={`${className}__listPanel__footer__content`}>
                                <Stack spacing={1}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePaginationClick}
                                        showFirstButton
                                        showLastButton
                                        size={is991px ? "small" : "medium"}
                                        className={`${className}__listPanel__footer__pagination`}
                                    />
                                </Stack>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Panel - Details */}
                <div className={`${className}__detailPanel ${
                    is0To800
                        ? (!!selectedNameCard && Object.keys(selectedNameCard).length > 0 && showUserCard
                            ? `${className}__detailPanel--open`
                            : `${className}__detailPanel--closed`)
                        : ''
                }`}>
                    {is0To800 && (
                        <IconButton
                            className={`${className}__detailPanel__closeBtn`}
                            onClick={handleShowUserCard}
                        >
                            <Close />
                        </IconButton>
                    )}
                    
                    {!!selectedNameCard && Object.keys(selectedNameCard).length > 0 ? (
                        (!is0To800 || showUserCard) && (
                            <NameCardDisplay
                                nameCard={selectedNameCard}
                                reloadData={renderData}
                                handleLabelAnalytics={props?.handleLabelAnalytics}
                            />
                        )
                    ) : (
                        !is0To800 && (
                            <div className={`${className}__detailPanel__empty`}>
                                <NameCardIcon className={`${className}__detailPanel__empty__icon`} />
                                <Typography variant="h6" sx={{ color: 'var(--color-primaryGrey)' }} gutterBottom>
                                    No Selection
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'var(--color-primaryGrey)' }}>
                                    Select a name card from the list to view details
                                </Typography>
                            </div>
                        )
                    )}
                </div>
            </div>

            {showNameCardForm && (
                <NameCardForm
                    open={showNameCardForm}
                    closePopup={toggleNameCardForm}
                    reloadData={renderData}
                />
            )}
        </div>
    );
}
